import { useMutation, useQueryClient } from '@tanstack/react-query';
import { privateApi } from '../../../api/api';

// Tipe untuk variabel yang diterima oleh fungsi mutasi
interface DetachPropertyVariables {
    userId: string;
    propertyId: string;
}

// Tipe untuk respons sukses dari API (diasumsikan sederhana)
// Kita akan buat skema Zod sederhana di sini untuk validasi.
import { z } from 'zod';

const DetachPropertyResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: z.any().optional(), // Data bisa beragam atau tidak ada sama sekali
});

type DetachPropertyResponse = z.infer<typeof DetachPropertyResponseSchema>;

// Endpoint API untuk operasi detach
const API_ENDPOINT = '/v4/user-property/detach';

/**
 * Hook kustom untuk melepaskan (detach) sebuah properti dari seorang pengguna (Versi 4).
 * Mengenkapsulasi logika mutasi, pemanggilan API, dan invalidasi cache.
 * 
 * @returns Hasil dari React Query `useMutation`.
 */
export const useDetachPropertyV4 = () => {
    const queryClient = useQueryClient();

    return useMutation<DetachPropertyResponse, Error, DetachPropertyVariables>({
        /**
         * Fungsi mutasi yang akan dijalankan saat `mutate()` dipanggil.
         * @param variables - Objek yang berisi `userId` dan `propertyId`.
         */
        mutationFn: async ({ userId, propertyId }: DetachPropertyVariables): Promise<DetachPropertyResponse> => {
            if (!userId || !propertyId) {
                throw new Error("User ID and Property ID are required to detach a property.");
            }

            try {
                // Melakukan request POST dengan body yang berisi ID.
                const response = await privateApi.post(API_ENDPOINT, {
                    user_id: userId,
                    property_id: propertyId
                });

                // Validasi struktur data respons dari API.
                const validationResult = DetachPropertyResponseSchema.safeParse(response.data);

                if (!validationResult.success) {
                    console.error("API response validation failed in useDetachPropertyV4:", validationResult.error.flatten());
                    throw new Error("Invalid data format received from the server after detaching property.");
                }

                return validationResult.data;
            } catch (error) {
                console.error('Failed to detach property (V4):', error);
                throw error;
            }
        },

        /**
         * Callback yang dijalankan setelah mutasi berhasil.
         * Logika invalidasi sama persis dengan 'attach' untuk memastikan konsistensi UI.
         */
        onSuccess: (_data, variables) => {
            const { userId } = variables;

            // 1. Invalidate query daftar pengguna untuk memperbarui tabel utama.
            queryClient.invalidateQueries({ queryKey: ['users', 'v4', 'list'] });

            // 2. Invalidate query detail pengguna yang diubah.
            queryClient.invalidateQueries({ queryKey: ['users', 'v4', 'detail', userId] });

            // 3. Invalidate query daftar semua properti.
            // Ini akan membuat properti yang baru dilepas muncul kembali di dropdown "tersedia".
            queryClient.invalidateQueries({ queryKey: ['properties', 'v4', 'all'] });
        },

        /**
         * Callback yang dijalankan jika mutasi gagal.
         */
        onError: () => {
            // No-op. Penanganan error utama ada di `mutationFn` dan di komponen yang memanggil.
        },
    });
};